<template>
    <div>
        <StructuredData />
        <section class="section-profile-cover section-shaped my-0">
            <div class="shape shape-style-1 shape-default shape-skew">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
            </div>
        </section>
        
        <section class="section section-skew">
            <div class="container">
                <card shadow class="card-profile mt--150" no-body>
                    <div class="px-4 py-5">
                        <div class="text-center mb-5">
                            <h1 class="display-3">KI-gestützte Softwareentwicklung</h1>
                            <p class="lead">
                                Moderne KI-Tools richtig einsetzen – mit fundierter Architektur-Expertise
                            </p>
                        </div>

                        <div class="row">
                            <div class="col-lg-8 mx-auto">
                                <h2>KI in der Softwareentwicklung: Chance und Risiko</h2>
                                <p>
                                    Tools wie GitHub Copilot, ChatGPT und Claude revolutionieren die 
                                    Softwareentwicklung. Sie beschleunigen die Entwicklung enorm – aber 
                                    nur, wenn man weiß, wie man sie richtig einsetzt.
                                </p>
                                <p>
                                    <strong>Das Problem:</strong> KI generiert Code schnell, aber nicht 
                                    automatisch guten Code. Ohne Architektur-Vorgaben entsteht oft 
                                    unstrukturierter, schwer wartbarer Code – technische Schulden von Anfang an.
                                </p>

                                <div class="alert alert-info mt-4">
                                    <strong>Unser Ansatz:</strong> Wir kombinieren über 15 Jahre Erfahrung 
                                    in Softwarearchitektur mit modernen KI-Tools. Das Ergebnis: schnellere 
                                    Entwicklung bei gleichzeitig hoher Code-Qualität.
                                </div>

                                <h3 class="mt-5">Warum Architektur-Expertise entscheidend ist</h3>
                                <p>
                                    KI-Tools sind nur so gut wie die Anweisungen, die sie bekommen. 
                                    Unsere Architektur-Expertise macht den Unterschied:
                                </p>
                                
                                <div class="row mt-4">
                                    <div class="col-md-6 d-flex">
                                        <card shadow class="mb-4 flex-fill">
                                            <div class="px-3 py-3">
                                                <h5 class="text-danger">
                                                    <i class="ni ni-fat-remove mr-2"></i>
                                                    Ohne Architektur-Vorgaben
                                                </h5>
                                                <ul class="mb-0">
                                                    <li>Inkonsistenter Code-Stil</li>
                                                    <li>Fehlende Design Patterns</li>
                                                    <li>Spaghetti-Code</li>
                                                    <li>Schwer testbar</li>
                                                    <li>Technische Schulden</li>
                                                </ul>
                                            </div>
                                        </card>
                                    </div>
                                    
                                    <div class="col-md-6 d-flex">
                                        <card shadow class="mb-4 flex-fill">
                                            <div class="px-3 py-3">
                                                <h5 class="text-success">
                                                    <i class="ni ni-check-bold mr-2"></i>
                                                    Mit Architektur-Expertise
                                                </h5>
                                                <ul class="mb-0">
                                                    <li>Einheitliche Code-Standards</li>
                                                    <li>SOLID-Prinzipien</li>
                                                    <li>Clean Architecture</li>
                                                    <li>Testbare Komponenten</li>
                                                    <li>Wartbarer Code</li>
                                                </ul>
                                            </div>
                                        </card>
                                    </div>
                                </div>

                                <h3 class="mt-5">Unsere KI-gestützten Services</h3>
                                
                                <div class="row mt-4">
                                    <div class="col-md-6 d-flex">
                                        <card shadow class="mb-4 flex-fill">
                                            <div class="px-3 py-3">
                                                <h5 class="text-primary">
                                                    <i class="ni ni-atom mr-2"></i>
                                                    KI-gestützte Entwicklung
                                                </h5>
                                                <p>
                                                    Schnellere Feature-Entwicklung durch intelligente 
                                                    Code-Generierung mit präzisen Architektur-Prompts.
                                                </p>
                                            </div>
                                        </card>
                                    </div>
                                    
                                    <div class="col-md-6 d-flex">
                                        <card shadow class="mb-4 flex-fill">
                                            <div class="px-3 py-3">
                                                <h5 class="text-success">
                                                    <i class="ni ni-glasses-2 mr-2"></i>
                                                    AI Code Review
                                                </h5>
                                                <p>
                                                    Automatisierte Code-Analyse kombiniert mit 
                                                    menschlicher Expertise für maximale Qualität.
                                                </p>
                                            </div>
                                        </card>
                                    </div>
                                    
                                    <div class="col-md-6 d-flex">
                                        <card shadow class="mb-4 flex-fill">
                                            <div class="px-3 py-3">
                                                <h5 class="text-warning">
                                                    <i class="ni ni-books mr-2"></i>
                                                    Dokumentation & Tests
                                                </h5>
                                                <p>
                                                    KI-unterstützte Generierung von Dokumentation, 
                                                    Unit-Tests und API-Spezifikationen.
                                                </p>
                                            </div>
                                        </card>
                                    </div>
                                    
                                    <div class="col-md-6 d-flex">
                                        <card shadow class="mb-4 flex-fill">
                                            <div class="px-3 py-3">
                                                <h5 class="text-info">
                                                    <i class="ni ni-settings-gear-65 mr-2"></i>
                                                    Legacy-Modernisierung
                                                </h5>
                                                <p>
                                                    KI-gestützte Analyse und Refactoring von 
                                                    bestehendem Code mit Architektur-Verbesserungen.
                                                </p>
                                            </div>
                                        </card>
                                    </div>
                                </div>

                                <h3 class="mt-5">Unser Qualitätsprozess</h3>
                                <ul class="list-unstyled mt-3">
                                    <li class="py-2">
                                        <icon name="ni ni-check-bold" class="text-success mr-2"></icon>
                                        <strong>Architektur-Vorgaben</strong> – Klare Strukturen vor der Code-Generierung
                                    </li>
                                    <li class="py-2">
                                        <icon name="ni ni-check-bold" class="text-success mr-2"></icon>
                                        <strong>Präzise Prompts</strong> – Design Patterns und Best Practices eingebaut
                                    </li>
                                    <li class="py-2">
                                        <icon name="ni ni-check-bold" class="text-success mr-2"></icon>
                                        <strong>Code Review</strong> – Jeder generierte Code wird geprüft
                                    </li>
                                    <li class="py-2">
                                        <icon name="ni ni-check-bold" class="text-success mr-2"></icon>
                                        <strong>Testing</strong> – Automatisierte Tests für Qualitätssicherung
                                    </li>
                                    <li class="py-2">
                                        <icon name="ni ni-check-bold" class="text-success mr-2"></icon>
                                        <strong>Refactoring</strong> – Kontinuierliche Verbesserung der Code-Qualität
                                    </li>
                                </ul>

                                <h3 class="mt-5">Tools & Technologien</h3>
                                <div class="mt-3">
                                    <h6 class="text-muted">KI-Tools</h6>
                                    <badge type="default" rounded class="mr-2 mb-2">GitHub Copilot</badge>
                                    <badge type="default" rounded class="mr-2 mb-2">Claude</badge>
                                    <badge type="default" rounded class="mr-2 mb-2">ChatGPT</badge>
                                    <badge type="default" rounded class="mr-2 mb-2">Cursor</badge>
                                    <badge type="default" rounded class="mr-2 mb-2">Windsurf</badge>
                                    
                                    <h6 class="text-muted mt-3">Architektur</h6>
                                    <badge type="primary" rounded class="mr-2 mb-2">Clean Architecture</badge>
                                    <badge type="primary" rounded class="mr-2 mb-2">SOLID</badge>
                                    <badge type="primary" rounded class="mr-2 mb-2">Domain-Driven Design</badge>
                                    <badge type="primary" rounded class="mr-2 mb-2">Design Patterns</badge>
                                    
                                    <h6 class="text-muted mt-3">Qualitätssicherung</h6>
                                    <badge type="success" rounded class="mr-2 mb-2">ESLint</badge>
                                    <badge type="success" rounded class="mr-2 mb-2">PHPStan</badge>
                                    <badge type="success" rounded class="mr-2 mb-2">SonarQube</badge>
                                    <badge type="success" rounded class="mr-2 mb-2">Jest</badge>
                                    <badge type="success" rounded class="mr-2 mb-2">PHPUnit</badge>
                                </div>

                                <h3 class="mt-5">Für wen ist das geeignet?</h3>
                                <div class="row mt-4">
                                    <div class="col-md-6">
                                        <ul>
                                            <li>Startups mit knappem Budget</li>
                                            <li>Unternehmen mit Fachkräftemangel</li>
                                            <li>Projekte mit engem Zeitplan</li>
                                        </ul>
                                    </div>
                                    <div class="col-md-6">
                                        <ul>
                                            <li>Legacy-Systeme modernisieren</li>
                                            <li>MVP schnell entwickeln</li>
                                            <li>Entwicklerteams entlasten</li>
                                        </ul>
                                    </div>
                                </div>

                                <div class="text-center mt-5">
                                    <base-button
                                        tag="a"
                                        href="/#kontakt"
                                        type="default"
                                        size="lg"
                                        icon="fa fa-envelope"
                                        @click.prevent="$router.push('/#kontakt')"
                                    >
                                        KI-Projekt besprechen
                                    </base-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </card>
            </div>
        </section>
    </div>
</template>

<script>
import StructuredData from '../components/StructuredData.vue'

export default {
    name: 'ki-softwareentwicklung',
    components: {
        StructuredData
    },
    head: {
        title: {
            inner: 'KI-gestützte Softwareentwicklung',
            separator: '|',
            complement: 'Web Services and More'
        },
        meta: [
            {
                name: 'description',
                content: 'KI-gestützte Softwareentwicklung mit Architektur-Expertise ✓ GitHub Copilot, Claude, ChatGPT ✓ Clean Code trotz KI ✓ Schneller entwickeln ✓ Jetzt anfragen!',
                id: 'desc'
            },
            {
                name: 'keywords',
                content: 'KI Softwareentwicklung, AI Software Development, KI Programmierung, GitHub Copilot, AI Code Review, KI gestützte Entwicklung, Softwarearchitektur KI, AI Coding'
            },
            {
                property: 'og:title',
                content: 'KI-gestützte Softwareentwicklung | Web Services and More'
            },
            {
                property: 'og:description',
                content: 'KI-gestützte Softwareentwicklung mit Architektur-Expertise. Schneller entwickeln mit GitHub Copilot, Claude & ChatGPT – ohne Qualitätsverlust.'
            },
            {
                property: 'og:type',
                content: 'website'
            },
            {
                property: 'og:url',
                content: 'https://www.wsam.biz/ki-softwareentwicklung'
            },
            {
                name: 'twitter:card',
                content: 'summary_large_image'
            },
            {
                name: 'twitter:title',
                content: 'KI-gestützte Softwareentwicklung | Web Services and More'
            },
            {
                name: 'twitter:description',
                content: 'KI-gestützte Softwareentwicklung mit Architektur-Expertise. Schneller entwickeln ohne Qualitätsverlust.'
            }
        ]
    }
}
</script>
